/**
 * @fileoverview Safari Extension Nudge Screen
 * 
 * Uses the same design as /safari-extension page but with skip support.
 * Only shown on iOS native platform during onboarding.
 */

import { Bookmark, DollarSign, ExternalLink } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { SAFARI_EXTENSION_INSTALL_URL } from '@/lib/urls';

interface SafariExtensionNudgeProps {
  onSetUp: () => void;
  onSkip: () => void;
}

export function SafariExtensionNudge({ onSetUp, onSkip }: SafariExtensionNudgeProps) {

  const handleEnableExtension = async () => {
    const url = SAFARI_EXTENSION_INSTALL_URL;
    try {
      if (Capacitor.isNativePlatform()) {
        await AppLauncher.openUrl({ url });
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('[SafariExtensionNudge] Failed to open browser:', err);
    }
    onSetUp();
  };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-black text-white">
      <style>{`
        @keyframes float-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .float-up { animation: float-up 0.7s ease-out both; }
        .float-up-1 { animation-delay: 0.1s; }
        .float-up-2 { animation-delay: 0.2s; }
        .float-up-3 { animation-delay: 0.35s; }
        .float-up-4 { animation-delay: 0.5s; }
        .gradient-text {
          background: linear-gradient(135deg, #FF5C4D 0%, #FF6F3B 50%, #FFB347 100%);
          background-size: 200% 200%;
          animation: gradient-shift 4s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .cta-btn {
          background: linear-gradient(135deg, #FF5C4D, #FF6F3B);
          transition: all 0.2s ease;
        }
        .cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 32px rgba(255,92,77,0.35);
        }
        .cta-btn:active { transform: scale(0.98); }
        .feature-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          transition: all 0.3s ease;
        }
      `}</style>

      {/* Ambient glow */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(255,92,77,0.12) 0%, transparent 70%)', filter: 'blur(40px)' }} />

      <div className="relative px-5 pb-12 max-w-lg mx-auto flex flex-col min-h-[100dvh]">
        {/* Badge */}
        <div className="flex justify-center pt-14 mb-6 float-up float-up-1">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#FF5C4D' }} />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: '#FF5C4D' }} />
            </span>
            Safari Extension
          </div>
        </div>

        {/* Hero */}
        <div className="mb-8 text-center float-up float-up-2">
          <h1 className="text-[2.2rem] font-extrabold leading-tight tracking-tight mb-3">
            <span className="text-white">Shop smarter</span>
            <br />
            <span className="gradient-text">without the tabs.</span>
          </h1>
          <p className="text-white/45 text-base leading-relaxed max-w-xs mx-auto">
            Save products and find better prices - all inside Safari while you browse.
          </p>
        </div>

        {/* Feature cards */}
        <div className="flex flex-col gap-3 mb-8 float-up float-up-3">
          <div className="feature-card rounded-2xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,92,77,0.12)' }}>
              <Bookmark className="w-5 h-5" style={{ color: '#FF5C4D' }} />
            </div>
            <div>
              <p className="font-semibold text-sm text-white mb-0.5">Save as you browse</p>
              <p className="text-xs text-white/40 leading-relaxed">One tap to save any product. No copying links, no switching apps.</p>
            </div>
          </div>
          <div className="feature-card rounded-2xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,179,71,0.12)' }}>
              <DollarSign className="w-5 h-5" style={{ color: '#FFB347' }} />
            </div>
            <div>
              <p className="font-semibold text-sm text-white mb-0.5">Find it cheaper</p>
              <p className="text-xs text-white/40 leading-relaxed">Instantly see if the same item costs less somewhere else.</p>
            </div>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* CTA */}
        <div className="float-up float-up-4 space-y-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.5rem)' }}>
          <button
            type="button"
            onClick={handleEnableExtension}
            className="cta-btn w-full h-14 rounded-2xl flex items-center justify-center gap-2.5 font-semibold text-base text-white"
          >
            <ExternalLink className="w-5 h-5" />
            Enable in Safari Settings
          </button>
          <button
            onClick={onSkip}
            className="w-full text-center text-[11px] text-white/25 py-1 active:text-white/50 transition-colors"
          >
            not now
          </button>
        </div>
      </div>
    </div>
  );
}
